import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import type { Options } from '@/components/molecules/SelectForm';
import { ActivityDivisionInfoData, DivisionInfoData } from '@/entities/DivisionInfoData';
import { CreateInvitationCodePayload, FreeAgentPayload, UpdateInvitationCodePayload } from '@/entities/LeaguePlayersData';
import { SeasonListItem } from '@/entities/SeasonList';
import { SendMessagePayload } from '@/entities/sendMessageData';
import {
	ACTIVE_TEAMS_QUERY_KEY,
	DISTRICTS_INFO_QUERY_KEY,
	FEATURED_LEAGUES_QUERY_KEY,
	INVITATION_CODE_QUERY_KEY,
	MLP_STRIP_QUERY_KEY,
	PARTNERS_OPPONENTS_STATS_QUERY_KEY,
	PLAYER_ACTIVITY_QUERY_KEY,
	PLAYER_GRAPH_STATS_QUERY_KEY,
	PLAYER_OVERALL_STATS_QUERY_KEY,
	SCHEDULED_EVENTS_QUERY_KEY,
	SCORE_FORMATS_QUERY_KEY,
	SEASON_DIVISION_SUBSTITUTE_PLAYERS_QUERY_KEY,
	SYSTEM_USERS_SEARCH_QUERY_KEY,
	TEAM_LEAGUE_MODULE_FOR,
	TEAM_PLAYERS_QUERY_KEY
} from '@/hooks/constants';
import {
	createInvitationCode,
	getPartnersOpponentsStats,
	getPlayerGraphStats,
	getPlayerOverallStats,
	setPlayerAsInjured,
	SetPlayerAsInjuredRequest,
	getScheduledEvents,
	updateInvitationCode,
	updateIsFreeAgent,
	getScoreFormats,
	getSeasonDivisionSubstitutePlayers,
	getSystemUsersSearch
} from '@/infrastructure/network/data/leagues';
import { SeasonDivisionItem } from '@/infrastructure/network/dto/DivisionDto';
import { getPlayerActivityController } from '@/interface-adapters/controllers/get-player-activity.controller';
import { getSeasonDivisionsController } from '@/interface-adapters/controllers/get-season-divisions.controller';
import { getSeasonsListController } from '@/interface-adapters/controllers/get-seasons-list.controller';
import { getActiveTeamsController } from '@/interface-adapters/controllers/get-active-teams.controller';
import { joinRequestController } from '@/interface-adapters/controllers/join-request.controller';
import { getDistrictsController } from '@/interface-adapters/controllers/leagues/get-districts.controller';
import { getFeaturedLeaguesController } from '@/interface-adapters/controllers/leagues/get-featured-leagues.controller';
import { getInvitationCodeController } from '@/interface-adapters/controllers/leagues/get-invitation-code.controller';
import { sendMessageController } from '@/interface-adapters/controllers/leagues/send-message.controller';
import { lookupStatesController } from '@/interface-adapters/controllers/lookups/get-states.controller';
import { getTeamLeaguesPlayersController } from '@/interface-adapters/controllers/players/get-team-leagues-players.controller';
import { useOrganizationStore } from '@/store/organization-store';
import { getSponsorsController } from '@/interface-adapters/controllers/leagues/get-sponsors.controller';
import { SponsorLevelWithSponsors } from '@/entities/Sponsor';
import { getMLPStripController } from '@/interface-adapters/controllers/leagues/get-mlp-events.controller';
import { SearchParams } from '@/entities/SearchParams';
import { useRouter } from 'next/navigation';

interface IsFreeAgentInterface {
	teamLeagueId: string;
	organizationId: string;
	teamId: string;
	requestPayload: FreeAgentPayload;
}
interface UpdateInvitationCodeInterface {
	teamLeagueId: string;
	organizationId: string;
	teamId: string;
	codeId: string;
	requestPayload: UpdateInvitationCodePayload;
}
interface CreateInvitationCodeInterface {
	teamLeagueId: string;
	organizationId: string;
	teamId: string;
	requestPayload: CreateInvitationCodePayload;
}

interface ApproveOrDeclinePlayerRequest {
	teamLeagueId: string;
	organizationId: string;
	teamId: string;
	tradeRequestUuid: string;
	action: string;
}

export const useStatesQuery = (countryId: number) => {
	return useQuery<Options[]>({
		queryKey: [`states=${countryId}`],
		queryFn: async () => {
			const response = await lookupStatesController(countryId.toString());

			if (response.statusCode !== 200) {
				throw new Error(`Failed to fetch states: ${response.statusCode}`);
			}

			return (
				response.data?.results.map((state) => ({
					value: state.id.toString(),
					label: state.title
				})) || []
			);
		},
		enabled: !!countryId,
		placeholderData: keepPreviousData
	});
};

export const useTeamPlayersQuery = (organizationUuid: string, teamLeagueUuid: string, teamUuid: string) => {
	return useQuery({
		queryKey: [TEAM_PLAYERS_QUERY_KEY, teamUuid],
		queryFn: () => getTeamLeaguesPlayersController(organizationUuid, teamLeagueUuid, { team_uuid: teamUuid })
	});
};

export const useSeasonsQuery = (organizationUuid: string, teamLeagueUuid: string) => {
	return useQuery<SeasonListItem[]>({
		queryKey: [`seasons`, organizationUuid, teamLeagueUuid],
		queryFn: async () => {
			const response = await getSeasonsListController(organizationUuid, teamLeagueUuid);

			if (!response || response.statusCode !== 200) {
				throw new Error(`Failed to fetch seasons: ${response?.statusCode || 'Unknown error'}`);
			}

			return response.data || [];
		},
		enabled: !!organizationUuid && !!teamLeagueUuid,
		placeholderData: []
	});
};

export const useSeasonDivisions = (organizationUuid: string, teamLeagueUuid: string, selectedSeason: string | null) => {
	return useQuery<SeasonDivisionItem[]>({
		queryKey: ['seasonDivisions', organizationUuid, teamLeagueUuid, selectedSeason],
		queryFn: async () => {
			if (!selectedSeason) throw new Error('No season selected');
			const response = await getSeasonDivisionsController(organizationUuid, teamLeagueUuid, selectedSeason);
			if (response.statusCode !== 200) {
				throw new Error('Failed to fetch season divisions');
			}
			return response.data || [];
		},
		enabled: !!organizationUuid && !!teamLeagueUuid && !!selectedSeason
	});
};

export const useSponsors = (teamLeagueUuid: string) => {
	return useQuery<SponsorLevelWithSponsors[]>({
		queryKey: ['sponsors', teamLeagueUuid],
		queryFn: async () => {
			const response = await getSponsorsController(teamLeagueUuid, TEAM_LEAGUE_MODULE_FOR);
			if (response.statusCode !== 200) {
				throw new Error('Failed to fetch season divisions');
			}
			return response.data || [];
		},
		enabled: !!teamLeagueUuid
	});
};

export const usePlayerActivity = (organizationUuid: string, teamLeagueUuid: string, selectedSeason: string | null, disabled?: boolean) => {
	return useQuery<ActivityDivisionInfoData[]>({
		queryKey: [PLAYER_ACTIVITY_QUERY_KEY, organizationUuid, teamLeagueUuid, selectedSeason],
		queryFn: async () => {
			if (!selectedSeason) throw new Error('No season selected');
			const response = await getPlayerActivityController(organizationUuid, teamLeagueUuid, selectedSeason);
			if (response.statusCode !== 200) {
				throw new Error('Failed to fetch player activity');
			}
			return response.data || [];
		},
		enabled: !!organizationUuid && !!teamLeagueUuid && !!selectedSeason && !disabled
	});
};

export const useInvitationCodeQuery = (teamLeagueUuid: string, organizationUuid: string, teamUuid: string) => {
	return useQuery({
		queryKey: [INVITATION_CODE_QUERY_KEY, teamLeagueUuid, organizationUuid, teamUuid],
		queryFn: async () => await getInvitationCodeController(teamLeagueUuid, organizationUuid, teamUuid),
		enabled: !!teamLeagueUuid || !!organizationUuid || !!teamUuid
	});
};

export const useJoinRequest = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: ApproveOrDeclinePlayerRequest) => {
			try {
				const apiResponse = await joinRequestController(
					data.organizationId,
					data.teamLeagueId,
					data.teamId,
					data.tradeRequestUuid,
					data.action
				);

				if (!apiResponse?.success) {
					throw new Error(`The request was not approved or declined`);
				}
			} catch (error) {
				throw new Error(`The request was not approved or declined`);
			}
		},
		onSuccess: (_data) => {
			queryClient.invalidateQueries({ queryKey: [PLAYER_ACTIVITY_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [ACTIVE_TEAMS_QUERY_KEY] });
			toast.success('Completed successfully.');
		},
		onError: () => {
			toast.error('An error has occurred. Please try again later.');
		}
	});
};

export const useUpdateIsFreeAgentMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: IsFreeAgentInterface) => {
			try {
				const apiResponse = await updateIsFreeAgent({
					teamLeagueUuid: data.teamLeagueId,
					organizationUuid: data.organizationId,
					teamUuid: data.teamId,
					requestPayload: data.requestPayload
				});

				if (apiResponse?.statusCode >= 400) {
					throw new Error(`HTTP error ${apiResponse.statusCode}`);
				}
			} catch (error) {
				throw new Error(`HTTP error ${error}`);
			}
		},
		onSuccess: (_data) => {
			queryClient.invalidateQueries({ queryKey: [ACTIVE_TEAMS_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [PLAYER_ACTIVITY_QUERY_KEY] });
			toast.success('Free agent status successfully updated.');
		},
		onError: () => {
			toast.error('An error has occurred. Please try again later.');
		}
	});
};

export const useSetPlayerAsInjuredMutate = () => {
	const router = useRouter();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: SetPlayerAsInjuredRequest) => {
			try {
				const apiResponse = await setPlayerAsInjured({
					teamLeagueId: data.teamLeagueId,
					organizationId: data.organizationId,
					teamId: data.teamId,
					requestPayload: data.requestPayload
				});

				if (apiResponse?.statusCode >= 400) {
					throw new Error(`HTTP error ${apiResponse.statusCode}`);
				}
			} catch (error) {
				throw new Error(`HTTP error ${error}`);
			}
		},
		onSuccess: (_data, { teamLeagueId }) => {
			queryClient.invalidateQueries({ queryKey: ['TEAM_LEAGUE_TEAM_PLAYERS'] });
			toast.success('Player successfully added to the injured reserve.');
			router.push(`/team-leagues/${teamLeagueId}/team-info/active-teams`);
		},
		onError: () => {
			toast.error('An error has occurred. Please try again later.');
		}
	});
};

export const useUpdateInvitationCodeMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: UpdateInvitationCodeInterface) => {
			try {
				const apiResponse = await updateInvitationCode({
					teamLeagueUuid: data.teamLeagueId,
					organizationUuid: data.organizationId,
					teamUuid: data.teamId,
					codeUuid: data.codeId,
					requestPayload: data.requestPayload
				});

				if (apiResponse?.statusCode >= 400) {
					throw new Error(`HTTP error ${apiResponse.statusCode}`);
				}
			} catch (error) {
				throw new Error(`HTTP error ${error}`);
			}
		},
		onSuccess: (_data, { teamLeagueId, organizationId, teamId }) => {
			queryClient.invalidateQueries({ queryKey: [INVITATION_CODE_QUERY_KEY, teamLeagueId, organizationId, teamId] });
			toast.success('Invitation code has been updated successfully.');
		},
		onError: () => {
			toast.error('An error has occurred. Please try again later.');
		}
	});
};

export const useCreateInvitationCodeMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: CreateInvitationCodeInterface) => {
			try {
				const apiResponse = await createInvitationCode({
					teamLeagueUuid: data.teamLeagueId,
					organizationUuid: data.organizationId,
					teamUuid: data.teamId,
					requestPayload: data.requestPayload
				});

				if (apiResponse?.statusCode >= 400) {
					throw new Error(`HTTP error ${apiResponse.statusCode}`);
				}
			} catch (error) {
				throw new Error(`HTTP error ${error}`);
			}
		},
		onSuccess: (_data, { teamLeagueId, organizationId, teamId }) => {
			queryClient.invalidateQueries({ queryKey: [INVITATION_CODE_QUERY_KEY, teamLeagueId, organizationId, teamId] });
			toast.success('Invitation code has been created successfully.');
		},
		onError: () => {
			toast.error('The invitation code is no longer valid. Please generate a new one and set an expiration date.');
		}
	});
};

interface SendMessageInterface {
	teamLeagueId: string;
	organizationId: string;
	requestPayload: SendMessagePayload;
}

export const useSendMessageMutate = () => {
	return useMutation({
		mutationFn: async (data: SendMessageInterface) => {
			try {
				const apiResponse = await sendMessageController(data.teamLeagueId, data.organizationId, data.requestPayload);

				if (apiResponse?.statusCode >= 400) {
					throw new Error(`HTTP error ${apiResponse.statusCode}`);
				}
			} catch (error) {
				throw new Error(`HTTP error ${error}`);
			}
		},
		onSuccess: () => {
			toast.success('The message was sent successfully.');
		},
		onError: () => {
			toast.error('There was an error sending the message. Please try again later.');
		}
	});
};

export const useFeaturedLeaguesQuery = () => {
	return useQuery({
		queryKey: [FEATURED_LEAGUES_QUERY_KEY],
		queryFn: () => getFeaturedLeaguesController(TEAM_LEAGUE_MODULE_FOR)
	});
};

export const useMLPStripQuery = () => {
	return useQuery({
		queryKey: [MLP_STRIP_QUERY_KEY],
		queryFn: () => getMLPStripController()
	});
};

export const useScoreFormatsQuery = () => {
	return useQuery({
		queryKey: [SCORE_FORMATS_QUERY_KEY],
		queryFn: () => getScoreFormats()
	});
};

export const usePlayerGraphStatsQuery = (playerUuid: string, searchParams: SearchParams) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [PLAYER_GRAPH_STATS_QUERY_KEY, store.teamLeagueUuid, store.organizationUuid, playerUuid, searchParams?.season],
		queryFn: () =>
			getPlayerGraphStats({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				playerUuid: playerUuid,
				searchParams: searchParams
			}),
		enabled: !!store.organizationUuid && !!store.teamLeagueUuid && !!playerUuid
	});
};

export const usePlayerOverallStatsQuery = (playerUuid: string, searchParams: SearchParams) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [PLAYER_OVERALL_STATS_QUERY_KEY, store.teamLeagueUuid, store.organizationUuid, playerUuid, searchParams?.season],
		queryFn: () =>
			getPlayerOverallStats({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				playerUuid: playerUuid,
				searchParams: searchParams
			}),
		enabled: !!store.organizationUuid && !!store.teamLeagueUuid && !!playerUuid
	});
};

export const usePartnersOpponentsStatsQuery = (playerUuid: string, formatType: string, playerType: string) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [PARTNERS_OPPONENTS_STATS_QUERY_KEY, store.teamLeagueUuid, store.organizationUuid, playerUuid, formatType, playerType],
		queryFn: () =>
			getPartnersOpponentsStats({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				playerUuid: playerUuid,
				formatType: formatType,
				playerType: playerType
			}),
		enabled: !!store.organizationUuid && !!store.teamLeagueUuid && !!playerUuid
	});
};

export const useScheduledEventsQuery = (
	seasonUuid: string | undefined,
	searchParams: SearchParams,
	allowFilters: boolean = true,
	shouldCallApi: boolean = true
) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [
			SCHEDULED_EVENTS_QUERY_KEY,
			store.teamLeagueUuid,
			store.organizationUuid,
			allowFilters ? searchParams?.search : '',
			allowFilters ? searchParams?.startDate : '',
			allowFilters ? searchParams?.endDate : ''
		],
		queryFn: () =>
			getScheduledEvents({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				seasonUuid: seasonUuid,
				searchParams: searchParams,
				allowFilters: allowFilters
			}),
		enabled: !!store.organizationUuid && !!store.teamLeagueUuid && !!seasonUuid && shouldCallApi
	});
};

export const useDistrictsInfoQuery = (teamLeagueUuid: string) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [DISTRICTS_INFO_QUERY_KEY, teamLeagueUuid, store.organizationUuid],
		queryFn: () => getDistrictsController(teamLeagueUuid, store.organizationUuid),
		enabled: !!(store.organizationUuid && teamLeagueUuid)
	});
};

export const useSystemUsersSearchQuery = (debouncedSearch: string) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [SYSTEM_USERS_SEARCH_QUERY_KEY, store.teamLeagueUuid, store.organizationUuid, debouncedSearch],
		queryFn: () => getSystemUsersSearch({ search: debouncedSearch }),
		enabled: !!debouncedSearch
	});
};

export const useSeasonDivisionSubstitutePlayersQuery = (matchupGroupUuid: string | null | undefined) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [SEASON_DIVISION_SUBSTITUTE_PLAYERS_QUERY_KEY, store.teamLeagueUuid, store.organizationUuid],
		queryFn: () => getSeasonDivisionSubstitutePlayers(store.organizationUuid, store.teamLeagueUuid, matchupGroupUuid ?? ''),
		enabled: !!matchupGroupUuid
	});
};

export const useActiveTeams = (organizationUuid: string, teamLeagueUuid: string, selectedSeason: string | null, disabled?: boolean) => {
	return useQuery<DivisionInfoData[]>({
		queryKey: [ACTIVE_TEAMS_QUERY_KEY, organizationUuid, teamLeagueUuid, selectedSeason],
		queryFn: async () => {
			if (!selectedSeason) throw new Error('No season selected');
			const response = await getActiveTeamsController(organizationUuid, teamLeagueUuid, selectedSeason);

			if (response.statusCode !== 200) {
				throw new Error('Failed to fetch active teams');
			}
			return response.data || [];
		},
		enabled: !!organizationUuid && !!teamLeagueUuid && !!selectedSeason && !disabled
	});
};
